import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMountTransition, usePreviousValue } from '@hero/hooks';
import { OnboardingCard } from '@hero/molecules';
import { GiftBasketMoreInformationModal } from '@hero/organisms';
import { useCreateCollection } from '@hero/services/product-service/collection/hooks';
import { useNotification, useOnboardingStore, useShoppingCart } from '@hero/store/hooks';

import { collectionNameFormData, collectionNameFormValidationSchema } from './constants';
import { EmptyCart } from './EmptyCart';
import { FloatingCartButton } from './FloatingCartButton';
import { WidgetFooter } from './Footer';
import ProductWidgetHeader from './Header';
import { ProductList } from './ProductList';
import * as S from './style';

function ShoppingCart({
  featureType = 'recognition',
  onRemoveProduct,
  onClearAll = () => {},
  showSaveAsCollectionButton,
  translation,
  floatingButton,
  browseShoppingButtonEnabled,
  onConfirmCart,
  products,
  components = {}
}) {
  const { opened, setOpened } = useShoppingCart();
  const { mutateAsync: createCollection } = useCreateCollection();
  const { showSuccess, showNotification } = useNotification();
  const { t } = useTranslation();
  const onboarding = useOnboardingStore();

  const [cardScrollContainerStyle, setCardScrollContainerStyle] = useState({
    transition: 'all 200ms ease-in'
  });
  const [productContainerStyle, setProductContainerStyle] = useState({});
  const [isWidgetOnboardingCardShown, setIsWidgetOnboardingCardShown] = useState(true);
  const [showMoreInformationModal, setShowMoreInformationModal] = useState(false);

  const isTransitioning = useMountTransition(opened, 300);
  const previousProducts = usePreviousValue(products);

  const onSaveAsCollection = async ({ formValues }) => {
    await createCollection({
      name: formValues.collectionName,
      products: products.map(product => ({
        id: product.merchantProductId,
        merchantProductId: product.merchantProductId,
        source: product?.source || undefined,
        images: product?.images.map(i => ({ id: i.id, url: i.url })),
        name: product?.name,
        brand: product?.brand,
        skus: product.skus.map(sku => ({
          code: sku.code,
          price: sku.price
        }))
      }))
    });

    showSuccess({
      message: t('saveCollectionSuccessText'),
      type: 'toast'
    });
  };

  const showCreateCollectionModal = () => {
    showNotification({
      title: t('createNewCollectionQuestion'),
      header: t('saveAsCollectionHeader'),
      formData: collectionNameFormData,
      formValidationSchema: collectionNameFormValidationSchema,
      notificationImage: <Icon name="collections" size="xxl" />,
      primaryButtonProps: { children: t('save') },
      secondaryButtonProps: { children: t('noThanks') },
      onSubmit: onSaveAsCollection
    });
  };

  const setStyleByScrollPosition = currPos => {
    if (opened) {
      const isVisible = currPos.y < -146;

      const shouldBeStyle = {
        transition: `all 200ms ${isVisible ? 'ease-in' : 'ease-out'}`
      };

      const shouldBeProductContainerStyle = {
        transition: `all 200ms ${isVisible ? 'ease-in' : 'ease-out'}`
      };

      if (JSON.stringify(shouldBeStyle) === JSON.stringify(cardScrollContainerStyle)) return;

      setCardScrollContainerStyle(shouldBeStyle);
      setProductContainerStyle(shouldBeProductContainerStyle);
    }
  };

  useEffect(() => {
    return () => {
      setOpened(false);
    };
  }, []);

  useEffect(() => {
    if (opened) {
      setStyleByScrollPosition({ y: -document.documentElement.scrollTop });
    }
  }, [opened]);

  useEffect(() => {
    if (previousProducts?.length === 0 && products?.length === 1) {
      setOpened(true);
    }
  }, [products]);

  useScrollPosition(
    ({ currPos }) => {
      setStyleByScrollPosition(currPos);
    },
    [opened, cardScrollContainerStyle]
  );

  const infoCardContent = (
    <Button variant="link" onClick={() => setShowMoreInformationModal(true)}>
      {t('moreInformation')}
    </Button>
  );

  return (
    <S.ShoppingCart style={{ right: !opened && !isTransitioning ? '-100%' : 0 }}>
      <FloatingCartButton
        products={products}
        opened={opened}
        isTransitioning={isTransitioning}
        handleToggle={setOpened}
        {...floatingButton}
      />
      <S.ProductCardDrawerWrapper
        style={cardScrollContainerStyle}
        openIn={opened && isTransitioning}
      >
        <S.ProductCardDrawer>
          <OnboardingCard
            styles={{
              wrapper: {
                left: -10,
                top: -10
              }
            }}
            cardOffset={[12, -12]}
            position="left-start"
            isCardShown={isWidgetOnboardingCardShown}
            title={t('theGiftChoice')}
            description={t('cardWidgetOnboardingCardDesc')}
            content={infoCardContent}
            setIsCardShown={setIsWidgetOnboardingCardShown}
            isInfoActive={onboarding?.[featureType]?.infoCardsActive}
            featureType={featureType}
          />
          <ProductWidgetHeader
            productCount={products.length}
            showSaveAsCollectionButton={showSaveAsCollectionButton}
            onSaveAsCollection={showCreateCollectionModal}
            onClose={() => setOpened(false)}
          />

          {products.length > 0 && (
            <S.ProductCardItemClearArea>
              <Typography variant="body1SemiBold" color="fg">
                {t('itemsCount', { count: products.length })}
              </Typography>
              <Button
                variant="link"
                type="button"
                onClick={() => {
                  if (onClearAll) onClearAll();
                }}
                color="neutral"
              >
                {t('clearAll')}
              </Button>
            </S.ProductCardItemClearArea>
          )}
          {products.length > 0 ? (
            <ProductList
              featureType={featureType}
              products={products}
              onRemoveProduct={onRemoveProduct}
              productContainerStyle={productContainerStyle}
            />
          ) : (
            <EmptyCart
              translation={translation}
              browseShoppingButtonEnabled={browseShoppingButtonEnabled}
            />
          )}
          {products.length > 0 && (
            <WidgetFooter
              products={products}
              components={components}
              onConfirmCart={onConfirmCart}
            />
          )}
        </S.ProductCardDrawer>
      </S.ProductCardDrawerWrapper>
      <GiftBasketMoreInformationModal
        isModalOpen={showMoreInformationModal}
        onRequestClose={() => setShowMoreInformationModal(false)}
      />
    </S.ShoppingCart>
  );
}

ShoppingCart.S = S;

export { ShoppingCart };
